// The improved version of the My Intrivity Application
import { html, css, LitElement } from 'lit-element';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { clone, cloneDeep } from 'lodash-es';
import { library, dom } from "@fortawesome/fontawesome-svg-core";   

import './behaviors_app.js';
import { a11yStyles, disclosureButton, uniqueId } from './a11y.js';
import { t, Localized } from './i18n.js';

export class IntrivityResultsAtGlanceApp extends Localized(LitElement) {

    static get styles() {
        return [a11yStyles, css`
            .uppercase-text {
                text-transform: uppercase;
            }
            .section-header {
                margin: 0 0 5px 0;
                width: 100%;
                box-sizing: border-box;
                color: black;
                font-size: 1.5em;
                font-weight: normal;
                padding: 15px 10px 10px 30px;
                background-color: gray;
            }
            .section-header .disclosure {
                vertical-align: middle;
                margin-left: 8px;
                padding: 4px;
            }
            /* These were CSS-grid stacks of <div>s. Real table markup means the
               row/column relationships are actually exposed to screen readers
               (WCAG 1.3.1) instead of being purely visual. */
            table.glance {
                border-collapse: collapse;
                font-size: inherit;
                /* Fill the wrapper so the 2-column attitude table lines up with
                   the 4-column characteristic table above it, the way the
                   original CSS-grid version did. */
                width: 100%;
            }
            table.glance caption {
                text-align: left;
                padding: 4px 0;
                font-weight: 600;
            }
            table.glance th,
            table.glance td {
                border: 1px solid black;
                padding: 5px;
                text-align: left;
                vertical-align: middle;
            }
            table.glance thead th {
                background-color: white;
                font-weight: normal;
            }
            table.glance .score {
                text-align: right;
                white-space: nowrap;
            }
            .glance-scroll {
                overflow-x: auto;
                max-width: 100%;
                margin: 20px;
            }
        `];
    }
    
    static get properties() {
        return {
            domain: { type: Object },
            domain_prefix: { type: String },
            report: { type: Object },
            expanded: { type: Boolean },
            printable: { type: Boolean }           // printable view
        };
    }

    constructor() {
        super();
        this.expanded = false;
        this._panelId = uniqueId('results-at-a-glance');
    }

    connectedCallback() {
        super.connectedCallback();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }
    
    render() {
        return html`
            <h2 class="section-header">
                ${t('domainDetails.atAGlance')}
                ${this.printable ? html``: disclosureButton(this.expanded, t('domainDetails.atAGlance'), this._panelId, () => { this.expanded = !this.expanded; })}
            </h2>
        
            ${this.expanded || this.printable ? html`
                <div id=${this._panelId} style="font-size: 1.2em; display: flex; flex-wrap: wrap; justify-content: center;">

                    <div class="glance-scroll" style="max-width: 300px;">
                        <table class="glance" style="background-color: ${this.domain.color};">
                            <caption class="sr-only">${t('domainDetails.domainRawCaption')}</caption>
                            <thead>
                                <tr>
                                    <th scope="col">${t('domainDetails.domainColumn')}</th>
                                    <th scope="col">${t('overview.rawScoreLine1')}<br />${t('overview.rawScoreLine2')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th scope="row" class="uppercase-text" style="font-weight: normal;">${this.domain.name}</th>
                                    <td class="score">${this.report[this.domain_prefix].average}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div>
                        <div class="glance-scroll" style="max-width: 600px;">
                            <table class="glance" style="font-size: smaller; background-color: ${this.domain.color};">
                                <caption class="sr-only">${t('domainDetails.behaviorCaption', { domain: this.domain.name })}</caption>
                                <thead>
                                    <tr>
                                        <th scope="col">${t('domainDetails.characteristic')}</th>
                                        <th scope="col">${t('overview.rawScoreLine1')}<br />${t('overview.rawScoreLine2')}</th>
                                        <th scope="col">${t('domainDetails.behaviorColumn')}</th>
                                        <th scope="col">${t('overview.rawScoreLine1')}<br />${t('overview.rawScoreLine2')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${this.domain.level_two.map((prop, i) => prop.behaviors.map((behavior, j) => html`
                                        <tr>
                                            ${j === 0 ? html`
                                                <th scope="rowgroup" rowspan=${prop.behaviors.length} class="uppercase-text" style="font-weight: normal;">${prop.name}</th>
                                                <td rowspan=${prop.behaviors.length} class="score">${this.report[this.domain_prefix].level_two[i].average}</td>
                                            `: html``}
                                            <th scope="row" style="font-weight: normal; font-size: smaller; background-color: hsl(${this.domain.colorhue} ${this.domain.colorsat}% ${this.domain.colorlight + 15.0}%);">${behavior.name}</th>
                                            <td class="score" style="font-size: smaller; background-color: hsl(${this.domain.colorhue} ${this.domain.colorsat}% ${this.domain.colorlight + 15.0}%);">
                                                ${this.report[this.domain_prefix].level_two[i].behaviors[j].average}
                                            </td>
                                        </tr>
                                    `))}
                                </tbody>
                            </table>
                        </div>

                        ${ this.report[this.domain_prefix].attitudes && this.report[this.domain_prefix].attitudes.length > 0 ? html`
                        <div class="glance-scroll" style="max-width: 600px;">
                            <table class="glance" style="font-size: smaller; background-color: ${this.domain.color};">
                                <caption class="sr-only">${t('domainDetails.attitudeCaption', { domain: this.domain.name })}</caption>
                                <thead>
                                    <tr>
                                        <th scope="col" style="font-style: italic;">${t('domainDetails.attitudeColumn')}</th>
                                        <th scope="col">${t('overview.rawScoreLine1')}<br />${t('overview.rawScoreLine2')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${this.domain.attitudes.map((attitude, i) => html`
                                        <tr>
                                            <th scope="row" style="font-weight: normal; font-style: italic; background-color: hsl(${this.domain.colorhue} ${this.domain.colorsat}% ${this.domain.colorlight + 30.0}%);">${attitude.name}</th>
                                            <td class="score" style="background-color: hsl(${this.domain.colorhue} ${this.domain.colorsat}% ${this.domain.colorlight + 30.0}%);">${this.report[this.domain_prefix].attitudes[i].average}</td>
                                        </tr>
                                    `)}
                                </tbody>
                            </table>
                        </div>
                        `: html``}
                    </div>

                </div>    
            `: html``}
        
        
        `
    } 
 
}
if (!customElements.get('intrivity-results-at-glance')) customElements.define('intrivity-results-at-glance', IntrivityResultsAtGlanceApp);
